import React, { useState, useEffect, useCallback } from "react";
import {
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  UserMinusIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import MetricCard from "../components/MetricCard";
import FilterBar from "../components/FilterBar";
import ChannelDistribution from "../components/ChannelDistribution";
import HeatMap from "../components/HeatMap";
import { metricsService } from "../services/api";

// Função para formatar minutos em hh:mm
const formatMinutesToHHMM = (minutes) => {
  if (!minutes || isNaN(minutes)) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [agentMetrics, setAgentMetrics] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('=== DASHBOARD GERAL - Loading with filters ===');
      console.log('Filters object:', JSON.stringify(filters, null, 2));
      console.log('teamId:', filters.teamId);
      console.log('conversationType:', filters.conversationType);
      console.log('inboxId:', filters.inboxId);
      console.log('agentId:', filters.agentId);

      // Carregar dados do dashboard e métricas de agentes em paralelo
      const [dashboardResponse, agentMetricsResponse] = await Promise.all([
        metricsService.getDashboard(filters),
        metricsService.getAgentMetrics(filters)
      ]);

      console.log('Dashboard response received');
      console.log('Agent metrics response received');

      setDashboardData(dashboardResponse.data.data);
      setAgentMetrics(agentMetricsResponse.data.data || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error loading dashboard:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleFilterChange = (newFilters) => {
    console.log('Filter change received:', newFilters);
    setFilters(newFilters);
  };

  const handleExportAgentConversations = (agentId) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.inboxId) params.append('inboxId', filters.inboxId);
    window.open(`${API_URL}/api/metrics/export/agent-conversations/${agentId}?${params.toString()}`, '_blank');
  };

  const handleExportAgentMetrics = () => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.inboxId) params.append('inboxId', filters.inboxId);
    window.open(`${API_URL}/api/metrics/export/agent-metrics?${params.toString()}`, '_blank');
  };

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-semibold mb-2">
            Erro ao carregar dashboard
          </h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const serviceLevel = dashboardData?.serviceLevel || {};
  const tma = dashboardData?.tma || {};
  const tme = dashboardData?.tme || {};
  const totals = dashboardData?.conversationTotals || {};

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Filtros */}
      <FilterBar
        onFilterChange={handleFilterChange}
        showConversationType={true}
        showTeams={true}
        showLabels={true}
      />

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <MetricCard
          title="Nível de Serviço"
          value={`${serviceLevel.service_level_percentage || 0}%`}
          subtitle={`${serviceLevel.conversations_within_sla || 0} de ${
            serviceLevel.total_conversations || 0
          } conversas`}
          icon={<ChartBarIcon className="w-6 h-6" />}
          color="blue"
          tooltip={{
            title: "Nível de Serviço (SLA) - Prazo: 24 horas",
            description: "Percentual de conversas atendidas dentro do prazo estabelecido pelo SLA (Service Level Agreement). O prazo de referência aplicado é de 24 horas para a primeira resposta ao cliente. Mede a eficiência do time em responder dentro dos prazos acordados.",
            formula: "(Conversas dentro do SLA / Total de conversas) × 100",
            example: "Se 85 de 100 conversas receberam primeira resposta em até 24 horas: (85/100) × 100 = 85%"
          }}
        />

        <MetricCard
          title="TMA - Tempo Médio de Atendimento"
          value={formatMinutesToHHMM(tma.tma_minutes)}
          subtitle={`${tma.total_resolved || 0} conversas resolvidas`}
          icon={<ClockIcon className="w-6 h-6" />}
          color="green"
          tooltip={{
            title: "Tempo Médio de Atendimento (TMA)",
            description: "Tempo médio desde a primeira resposta do agente até a resolução da conversa. Indica a eficiência na resolução de problemas dos clientes.",
            formula: "Soma do tempo de atendimento / Total de conversas resolvidas",
            example: "Se 10 conversas foram resolvidas em 300 minutos total: 300/10 = 30 minutos (00:30)"
          }}
        />

        <MetricCard
          title="TME - Tempo Médio de Espera"
          value={formatMinutesToHHMM(tme.tme_minutes)}
          subtitle={`${tme.total_conversations || 0} conversas`}
          icon={<UserGroupIcon className="w-6 h-6" />}
          color="yellow"
          tooltip={{
            title: "Tempo Médio de Espera (TME)",
            description: "Tempo médio que o cliente aguarda desde o início da conversa até receber a primeira resposta do agente. Métrica importante para avaliar a velocidade do primeiro contato.",
            formula: "Soma do tempo de espera / Total de conversas",
            example: "Se clientes aguardaram 150 minutos no total em 10 conversas: 150/10 = 15 minutos (00:15)"
          }}
        />
      </div>

      {/* Cards de Totais de Conversas */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">Visão Geral de Conversas</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            title="Total"
            value={totals.total_conversations || 0}
            subtitle="100%"
            icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
            color="gray"
            tooltip={{
              title: "Total de Conversas",
              description: "Número total de conversas criadas no período selecionado, independente do status ou atribuição."
            }}
          />
          <MetricCard
            title="Abertas"
            value={totals.total_open || 0}
            subtitle={`${((totals.total_open / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<XCircleIcon className="w-5 h-5" />}
            color="orange"
            tooltip={{
              title: "Conversas Abertas",
              description: "Conversas que ainda não foram resolvidas (status: aberta, pendente ou snoozed)."
            }}
          />
          <MetricCard
            title="Fechadas"
            value={totals.total_closed || 0}
            subtitle={`${((totals.total_closed / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<CheckCircleIcon className="w-5 h-5" />}
            color="green"
            tooltip={{
              title: "Conversas Fechadas",
              description: "Conversas que foram resolvidas e fechadas."
            }}
          />
          <MetricCard
            title="Com Agente"
            value={totals.with_agent || 0}
            subtitle={`${((totals.with_agent / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<UserIcon className="w-5 h-5" />}
            color="blue"
            tooltip={{
              title: "Conversas com Agente",
              description: "Conversas que possuem um agente atribuído."
            }}
          />
          <MetricCard
            title="Sem Agente"
            value={totals.without_agent || 0}
            subtitle={`${((totals.without_agent / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<UserMinusIcon className="w-5 h-5" />}
            color="red"
            tooltip={{
              title: "Conversas sem Agente",
              description: "Conversas que ainda não foram atribuídas a nenhum agente."
            }}
          />
          <MetricCard
            title="Sem Resposta"
            value={totals.without_first_reply || 0}
            subtitle={`${((totals.without_first_reply / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<ExclamationTriangleIcon className="w-5 h-5" />}
            color="red"
            tooltip={{
              title: "Conversas Sem Primeira Resposta",
              description: "Conversas que ainda não receberam a primeira resposta de um agente. Estas conversas NÃO entram no cálculo do Nível de Serviço acima."
            }}
          />
        </div>
      </div>

      {/* Gráficos e Visualizações */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChannelDistribution data={dashboardData?.channelDistribution || []} />

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Ranking de Canais</h3>
          {dashboardData?.channelDistribution &&
          dashboardData.channelDistribution.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.channelDistribution
                .sort((a, b) => b.total_conversations - a.total_conversations)
                .map((channel, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">
                          {channel.channel_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {channel.channel_type}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        {channel.total_conversations}
                      </p>
                      <p className="text-sm text-gray-500">
                        {channel.percentage}%
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-gray-500">Nenhum dado disponível</p>
          )}
        </div>
      </div>

      {/* Mapa de Calor */}
      <div className="mb-6">
        <HeatMap data={dashboardData?.heatMap || dashboardData?.heatmap} />
      </div>

      {/* Tabela de Métricas por Agente */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Métricas por Agente</h3>
          <button
            onClick={handleExportAgentMetrics}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar métricas de todos os agentes"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Exportar Métricas
          </button>
        </div>
        {agentMetrics && agentMetrics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receptivas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ativas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abertas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolvidas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pendentes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adiadas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TME (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TMA (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <span>SLA %</span>
                      <div className="relative group">
                        <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="hidden group-hover:block absolute right-0 top-6 z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg normal-case font-normal">
                          <div className="font-semibold mb-1">Nível de Serviço do Agente</div>
                          <div className="text-gray-300">
                            Percentual de conversas do agente que receberam primeira resposta em até 24 horas (prazo de SLA).
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-700 text-gray-300">
                            <div className="font-semibold text-white">Cores:</div>
                            <div className="mt-1 space-y-1">
                              <div>🟢 Verde: ≥ 80% (Excelente)</div>
                              <div>🟡 Amarelo: 60-79% (Atenção)</div>
                              <div>🔴 Vermelho: &lt; 60% (Crítico)</div>
                            </div>
                          </div>
                          <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agentMetrics.map((agent, index) => {
                  const slaPercentage = (
                    (agent.conversations_within_sla /
                      agent.total_conversations) *
                    100
                  ).toFixed(1);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.agent_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.total_conversations}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-indigo-100 text-indigo-800">
                          {agent.receptive_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">
                          {agent.active_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                          {agent.open_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
                          {agent.resolved_conversations}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
                          {agent.pending_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-100 text-orange-800">
                          {agent.snoozed_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMinutesToHHMM(agent.avg_first_response_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMinutesToHHMM(agent.avg_resolution_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded ${
                            slaPercentage >= 80
                              ? "bg-green-100 text-green-800"
                              : slaPercentage >= 60
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {slaPercentage}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleExportAgentConversations(agent.agent_id)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title={`Exportar conversas de ${agent.agent_name}`}
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">Nenhum dado disponível</p>
        )}
      </div>

      {/* Rodapé */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Última atualização: {lastUpdate.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
      </div>
    </div>
  );
};

export default Dashboard;
